import React from "react";
import { BrowserRouter as Router, Routes, Route, NavLink } from "react-router-dom";
import { AiOutlineHome, AiOutlineFileAdd } from "react-icons/ai";
import { GiTakeMyMoney } from "react-icons/gi";
import { MdSpaceDashboard } from "react-icons/md";
import { FiSliders } from "react-icons/fi";
import { HiOutlineReceiptPercent } from "react-icons/hi2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles.css";

/* ---------------- Utilities ---------------- */
const todayStr = () => new Date().toISOString().slice(0, 10);
const toNumber = (v) => Number(String(v).replace(/[^0-9.]/g, "")) || 0;

function bucketOf(dateStr, period = "month") {
  // Parse safely to avoid timezone shifts
  const [y, m, d] = (dateStr || "").split("-").map(Number);
  if (!y || !m || !d) return "—";

  if (period === "day") {
    // Return the exact string the user chose; no UTC conversion
    return dateStr;
  }
  if (period === "week") {
    // Build from UTC parts to keep week math stable across timezones
    const tmp = new Date(Date.UTC(y, m - 1, d));
    tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7)); // Thursday of this ISO week
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
    return `${tmp.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
  }
  if (period === "year") return String(y);
  // month
  return `${y}-${String(m).padStart(2, "0")}`;
}

/* ---------------- Local Storage Hook ---------------- */
function useLocalStorage(key, initialValue) {
  const [state, setState] = React.useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : initialValue;
    } catch {
      return initialValue;
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {}
  }, [key, state]);
  return [state, setState];
}

/* ---------------- Seed Data ---------------- */
const SEED = [
  { id: 1, date: "2025-10-01", category: "Food", amount: 8.5, note: "Coffee & bagel" },
  { id: 2, date: "2025-10-02", category: "Transport", amount: 3.2, note: "BTS" },
  { id: 3, date: "2025-10-03", category: "Bills", amount: 45.0, note: "Electricity" },
  { id: 4, date: "2025-10-04", category: "Entertainment", amount: 12.0, note: "Movie" },
  { id: 5, date: "2025-10-05", category: "Groceries", amount: 25.7, note: "Big C" },
];

/* ---------------- Components ---------------- */
function ExpenseForm({ onSave, editing, onCancel }) {
  const [form, setForm] = React.useState({ date: todayStr(), category: "", amount: "", note: "" });

  React.useEffect(() => {
    if (editing) {
      setForm({
        date: editing.date,
        category: editing.category,
        amount: editing.amount,
        note: editing.note || "",
      });
    } else {
      setForm({ date: todayStr(), category: "", amount: "", note: "" });
    }
  }, [editing]);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === "amount" ? value.replace(/[^0-9.]/g, "") : value }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.date || !form.category || !form.amount) return;
    onSave({ ...form, amount: toNumber(form.amount) });
  };

  return (
    <form className="card" onSubmit={submit}>
      <h3 className="card-title"><AiOutlineFileAdd className="icon" /> {editing ? "Edit Expense" : "Add Expense"}</h3>
      <div className="grid">
        <label>
          <span>Date</span>
          <input type="date" name="date" value={form.date} onChange={change} required />
        </label>
        <label>
          <span>Category</span>
          <input name="category" value={form.category} onChange={change} required />
        </label>
        <label>
          <span>Amount</span>
          <input name="amount" inputMode="decimal" value={form.amount} onChange={change} required />
        </label>
        <label className="full">
          <span>Note</span>
          <input name="note" value={form.note} onChange={change} placeholder="Optional" />
        </label>
      </div>
      <div className="row">
        <button type="submit" className="primary">{editing ? "Update" : "Add"}</button>
        {editing && <button type="button" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}

function Filters({ filters, setFilters, categories }) {
  const upd = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="card">
      <h3 className="card-title"><FiSliders className="icon" /> Filters</h3>
      <div className="grid">
        <label>
          <span>From</span>
          <input type="date" value={filters.from} onChange={upd("from")} />
        </label>
        <label>
          <span>To</span>
          <input type="date" value={filters.to} onChange={upd("to")} />
        </label>
        <label className="full">
          <span>Category</span>
          <select
            value={filters.category}
            onChange={upd("category")}
            className="filter-select"
          >
            <option value="">All</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

/* --- Currency Formatting Helper --- */
const formatCurrency = (n, currency) => {
  if (currency === "MMK") {
    return n.toLocaleString("en-MM", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Ks";
  } else if (currency === "THB") {
    return n.toLocaleString("th-TH", { style: "currency", currency: "THB", minimumFractionDigits: 2 });
  } else if (currency === "JPY") {
    return n.toLocaleString("ja-JP", { style: "currency", currency: "JPY", minimumFractionDigits: 0 });
  } else {
    return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
  }
};

/* --- Expenses Table --- */
function ExpenseTable({ items, onEdit, onDelete, sort, onSortChange, currency }) {
  const fmt = (n) => formatCurrency(n, currency);

  const [exportScope, setExportScope] = React.useState("all"); // 'all' | 'month' | 'year'
  const [exportMonth, setExportMonth] = React.useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [exportYear, setExportYear] = React.useState(new Date().getFullYear().toString()); // YYYY

  const getExportRows = () => {
    if (exportScope === "month") return items.filter((x) => x.date.slice(0, 7) === exportMonth);
    if (exportScope === "year") return items.filter((x) => x.date.slice(0, 4) === exportYear);
    return items;
  };

  const exportPDF = () => {
    const rowsToExport = getExportRows();
    const title = `Expenses_${exportScope === "all" ? "All" : exportScope === "month" ? exportMonth : exportYear}`;
    const rows = rowsToExport.map(
      (x) =>
        `<tr>
          <td>${x.date}</td>
          <td>${x.category}</td>
          <td style="text-align:right">${fmt(x.amount)}</td>
          <td>${x.note || ""}</td>
        </tr>`
    ).join("");

    const html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${title}</title>
  <style>
    @media print { @page { size: A4 landscape; margin: 12mm; } }
    body{ font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; color:#111827; }
    h1{ font-size:20px; margin:0 0 12px; }
    .meta{ color:#6b7280; font-size:12px; margin-bottom:10px; }
    table{ width:100%; border-collapse:collapse; }
    th, td{ padding:8px 10px; border-bottom:1px solid #e5e7eb; font-size:13px; }
    th{ background:#f3f4f6; text-align:left; }
    tfoot td{ font-weight:700; }
  </style>
</head>
<body>
  <h1>Expense Report (${exportScope === "all" ? "All" : exportScope === "month" ? exportMonth : exportYear})</h1>
  <div class="meta">Exported: ${new Date().toLocaleString()} • Currency: ${currency} • Rows: ${rowsToExport.length}</div>
  <table>
    <thead>
      <tr><th>Date</th><th>Category</th><th style="text-align:right">Amount</th><th>Note</th></tr>
    </thead>
    <tbody>${rows || `<tr><td colspan="4">No expenses</td></tr>`}</tbody>
    <tfoot>
      <tr>
        <td colspan="2">Total</td>
        <td style="text-align:right">
          ${fmt(rowsToExport.reduce((s, r) => s + r.amount, 0))}
        </td>
        <td></td>
      </tr>
    </tfoot>
  </table>
  <script>window.onload = () => window.print();</script>
</body>
</html>
    `.trim();

  const w = window.open("", "_blank");
  if (w) {
    w.document.open();
    w.document.write(html);
    w.document.close();
  }
};

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
        <h3 className="card-title"><HiOutlineReceiptPercent className="icon" /> Expenses</h3>

        <div className="row" style={{ gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <span className="sort-label">Sort</span>
            <select
              value={sort}
              onChange={(e) => onSortChange(e.target.value)}
              className="sort-select"
            >
              <option value="date:desc">Newest</option>
              <option value="date:asc">Oldest</option>
              <option value="amount:desc">Amount ↓</option>
              <option value="amount:asc">Amount ↑</option>
              <option value="category:asc">Category A–Z</option>
              <option value="date:day:asc">Day ↑</option>
              <option value="date:day:desc">Day ↓</option>
              <option value="date:month:asc">Month ↑</option>
              <option value="date:month:desc">Month ↓</option>
              <option value="date:year:asc">Year ↑</option>
              <option value="date:year:desc">Year ↓</option>
            </select>
          </div>

          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <span className="sort-label">Export</span>
            <select
              value={exportScope}
              onChange={(e) => setExportScope(e.target.value)}
              className="sort-select"
            >
              <option value="all">All</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>

            {exportScope === "month" && (
              <input
                type="month"
                value={exportMonth}
                onChange={(e) => setExportMonth(e.target.value)}
                className="sort-select"
                style={{ paddingRight: 8 }}
              />
            )}

            {exportScope === "year" && (
              <input
                type="number"
                min="1970"
                max="9999"
                step="1"
                value={exportYear}
                onChange={(e) => setExportYear(e.target.value.slice(0, 4))}
                className="sort-select"
                style={{ width: 100, textAlign: "center" }}
              />
            )}

            <button onClick={exportPDF} className="export-btn">📄 Export PDF</button>
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Category</th>
              <th className="right">Amount</th>
              <th>Note</th>
              <th className="right"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id}>
                <td>{x.date}</td>
                <td>{x.category}</td>
                <td className="right">{fmt(x.amount)}</td>
                <td>{x.note}</td>
                <td className="right">
                  <button onClick={() => onEdit(x)} style={{ marginRight: 6 }}>Edit</button>
                  <button className="danger" onClick={() => onDelete(x.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="muted">No data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --- Dashboard --- */
function Dashboard({ rows, period, setPeriod, currency, onResetData }) {
  const fmt = (n) => formatCurrency(n, currency);
  const total = rows.reduce((s, r) => s + r.amount, 0);

  const byCat = Object.values(
    rows.reduce((acc, r) => {
      acc[r.category] ??= { category: r.category, total: 0 };
      acc[r.category].total += r.amount;
      return acc;
    }, {})
  ).sort((a, b) => b.total - a.total);

  const byPeriod = Object.values(
    rows.reduce((acc, r) => {
      const b = bucketOf(r.date, period);
      acc[b] ??= { bucket: b, total: 0 };
      acc[b].total += r.amount;
      return acc;
    }, {})
  ).sort((a, b) => b.bucket.localeCompare(a.bucket));
  const maxBar = Math.max(1, ...byCat.map((x) => x.total));

  return (
    <div className="card" style={{ maxHeight: 420 /* keep filters visible a bit */ }}>
      <div className="row between" style={{ marginBottom: 8 }}>
        <h3 className="card-title"><MdSpaceDashboard className="icon" /> Dashboard</h3>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className={period === "day" ? "primary" : ""} onClick={() => setPeriod("day")}>Day</button>
          <button className={period === "week" ? "primary" : ""} onClick={() => setPeriod("week")}>Week</button>
          <button className={period === "month" ? "primary" : ""} onClick={() => setPeriod("month")}>Month</button>
          {/* Reset Data (expenses) */}
          <button className="danger" onClick={onResetData} title="Delete all expenses">Reset Data</button>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
        <div className="stat">
          <div className="label">Total</div>
          <div className="value">{fmt(total)}</div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 8, gap: 16 }}>
        {/* By Category (scroll-hint) */}
        <div className="scroll-hint" style={{ maxHeight: 260, overflowY: "auto", paddingRight: 8 }}>
          <div className="label" style={{ marginBottom: 6 }}>By Category</div>
          {byCat.map((x) => (
            <div key={x.category} className="bar-row">
              <span>{x.category}</span>
              <div className="bar"><div className="bar-fill" style={{ width: `${(x.total / maxBar) * 100}%` }} /></div>
              <span>{fmt(x.total)}</span>
            </div>
          ))}
          {byCat.length === 0 && <div className="muted">No categories in range</div>}
        </div>

        {/* By Period (scroll-hint) */}
        <div className="scroll-hint" style={{ maxHeight: 260, overflowY: "auto", paddingRight: 8 }}>
          <div className="label" style={{ marginBottom: 6 }}>
            By {period[0].toUpperCase() + period.slice(1)}
          </div>
          <ul className="list">
            {byPeriod.map((b) => (
              <li key={b.bucket} className="list-row">
                <span>{b.bucket}</span>
                <span>{fmt(b.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* --- Pages --- */
function Home({
  visible,
  filters,
  setFilters,
  categories,
  addOrUpdate,
  editing,
  setEditing,
  period,
  setPeriod,
  currency,
  onResetData
}) {
  return (
    <>
      <Dashboard
        rows={visible}
        period={period}
        setPeriod={setPeriod}
        currency={currency}
        onResetData={onResetData}
      />
      <div className="two-col">
        <div className="col">
          <Filters filters={filters} setFilters={setFilters} categories={categories} />
        </div>
        <div className="col">
          <ExpenseForm onSave={addOrUpdate} editing={editing} onCancel={() => setEditing(null)} />
        </div>
      </div>
    </>
  );
}

function ExpensesPage({ visible, onEdit, onDelete, filters, setFilters, currency }) {
  const onSortChange = (val) => setFilters((f) => ({ ...f, sort: val }));
  return (
    <ExpenseTable
      items={visible}
      onEdit={onEdit}
      onDelete={onDelete}
      sort={filters.sort}
      onSortChange={onSortChange}
      currency={currency}
    />
  );
}

/* --- Main App --- */
export default function App() {
  const [expenses, setExpenses] = useLocalStorage("expenses", SEED);
  const [filters, setFilters] = React.useState({ from: "", to: "", category: "", sort: "date:desc" });
  const [editing, setEditing] = React.useState(null);
  const [period, setPeriod] = React.useState("month");
  const [currency, setCurrency] = React.useState("USD");
  const [theme, setTheme] = useLocalStorage("theme", "light");

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

  const visible = React.useMemo(() => {
    const from = filters.from || "1970-01-01";
    const to = filters.to || "9999-12-31";
    let rows = expenses.filter(
      (e) => e.date >= from && e.date <= to && (!filters.category || e.category === filters.category)
    );

    // Enhanced sorting
    const parts = filters.sort.split(":"); // ["date","month","asc"] etc.
    const primary = parts[0];
    const secondary = parts[1];
    const tertiary = parts[2];
    const dir = (tertiary || secondary) === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      if (primary === "amount") return (a.amount - b.amount) * dir;
      if (primary === "category") return a.category.localeCompare(b.category) * dir;

      if (secondary === "day") return a.date.localeCompare(b.date) * dir;
      if (secondary === "month") {
        const am = a.date.slice(0, 7), bm = b.date.slice(0, 7);
        return am.localeCompare(bm) * dir || a.date.localeCompare(b.date) * dir;
      }
      if (secondary === "year") {
        const ay = a.date.slice(0, 4), by = b.date.slice(0, 4);
        return ay.localeCompare(by) * dir || a.date.localeCompare(b.date) * dir;
      }
      return a.date.localeCompare(b.date) * dir;
    });

    return rows;
  }, [expenses, filters]);

  const categories = React.useMemo(
    () => Array.from(new Set(expenses.map((e) => e.category))).sort(),
    [expenses]
  );

  const addOrUpdate = (payload) => {
    if (editing) {
      setExpenses((prev) => prev.map((x) => (x.id === editing.id ? { ...editing, ...payload } : x)));
      toast.info("Expense updated ✅");
      setEditing(null);
    } else {
      const id = Math.max(0, ...expenses.map((e) => e.id)) + 1;
      setExpenses((prev) => [...prev, { id, ...payload }]);
      toast.success("Expense added 🎉");
    }
  };

  const remove = (id) => {
    setExpenses((prev) => prev.filter((x) => x.id !== id));
    toast.error("Expense deleted 🗑️");
  };

  // NEW: Reset all expenses data
  const resetData = React.useCallback(() => {
    if (window.confirm("This will delete ALL expenses. Continue?")) {
      setExpenses([]);
      toast.warn("All expenses have been cleared.");
    }
  }, [setExpenses]);

  return (
    <Router>
      <div className="container">
        <nav className="navbar card row between">
          <div className="row" style={{ alignItems: "center", gap: "10px" }}>
            <div className="brand">💸 Expense Tracker</div>

            {/* Currency */}
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="currency-select">
              <option value="USD">🇺🇸 USD $</option>
              <option value="THB">🇹🇭 THB ฿</option>
              <option value="MMK">🇲🇲 MMK Ks</option>
              <option value="JPY">🇯🇵 JPY ¥</option>
            </select>

            {/* Theme toggle */}
            <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === "dark" ? "🌙 Dark" : "🌞 Light"}
            </button>
          </div>

          <div className="row" style={{ gap: "16px" }}>
            <NavLink to="/" end className={({ isActive }) => "navlink" + (isActive ? " active" : "")}>
              <AiOutlineHome className="nav-icon" /> Home
            </NavLink>
            <NavLink to="/expenses" className={({ isActive }) => "navlink" + (isActive ? " active" : "")}>
              <GiTakeMyMoney className="nav-icon" /> Expenses
            </NavLink>
          </div>
        </nav>

        <Routes>
          <Route
            path="/"
            element={
              <Home
                visible={visible}
                filters={filters}
                setFilters={setFilters}
                categories={categories}
                addOrUpdate={addOrUpdate}
                editing={editing}
                setEditing={setEditing}
                period={period}
                setPeriod={setPeriod}
                currency={currency}
                onResetData={resetData}
              />
            }
          />
          <Route
            path="/expenses"
            element={
              <ExpensesPage
                visible={visible}
                onEdit={setEditing}
                onDelete={remove}
                filters={filters}
                setFilters={setFilters}
                currency={currency}
              />
            }
          />
        </Routes>

        <ToastContainer position="top-right" autoClose={1800} theme={theme} />
      </div>
    </Router>
  );
}